import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";

type EstablishmentLocation = {
  establishmentLatitude?: number;
  establishmentLongitude?: number;
  establishmentLocationName?: string;
  establishmentAddress?: string;
};

export default function EstablishmentLocationConfig() {
  const { error, success } = useToast();
  const [location, setLocation] = useState<EstablishmentLocation>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .get<EstablishmentLocation>("/tenant/establishment-location")
      .then(({ data }) => {
        if (!cancelled) setLocation(data);
      })
      .catch((err) => {
        if (!cancelled) error(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [error]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.patch("/tenant/establishment-location", location);
      success("Localização do estabelecimento atualizada com sucesso!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="app-page-compact text-slate-500">Carregando...</div>;
  }

  const hasCoordinates =
    Number.isFinite(location.establishmentLatitude) &&
    Number.isFinite(location.establishmentLongitude);

  return (
    <div className="app-page-compact">
      <BackButton />
      <h1 className="app-page-title mb-2">Localização do estabelecimento</h1>
      <p className="mb-6 text-sm text-slate-500">
        Configure os dados usados pelo botão de envio de localização nos
        atendimentos.
      </p>

      <form onSubmit={handleSubmit} className="app-card space-y-4 p-4 sm:p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="form-label" htmlFor="establishmentLocationName">
              Nome do estabelecimento
            </label>
            <input
              id="establishmentLocationName"
              className="form-input"
              value={location.establishmentLocationName ?? ""}
              onChange={(event) =>
                setLocation((current) => ({
                  ...current,
                  establishmentLocationName: event.target.value,
                }))
              }
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="establishmentAddress">
              Endereço
            </label>
            <input
              id="establishmentAddress"
              className="form-input"
              value={location.establishmentAddress ?? ""}
              onChange={(event) =>
                setLocation((current) => ({
                  ...current,
                  establishmentAddress: event.target.value,
                }))
              }
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="establishmentLatitude">
              Latitude
            </label>
            <input
              id="establishmentLatitude"
              type="number"
              min={-90}
              max={90}
              step="any"
              className="form-input"
              value={location.establishmentLatitude ?? ""}
              onChange={(event) =>
                setLocation((current) => ({
                  ...current,
                  establishmentLatitude:
                    event.target.value === ""
                      ? undefined
                      : event.target.valueAsNumber,
                }))
              }
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="establishmentLongitude">
              Longitude
            </label>
            <input
              id="establishmentLongitude"
              type="number"
              min={-180}
              max={180}
              step="any"
              className="form-input"
              value={location.establishmentLongitude ?? ""}
              onChange={(event) =>
                setLocation((current) => ({
                  ...current,
                  establishmentLongitude:
                    event.target.value === ""
                      ? undefined
                      : event.target.valueAsNumber,
                }))
              }
              required
            />
          </div>
        </div>

        {hasCoordinates && (
          <a
            href={`https://www.google.com/maps?q=${location.establishmentLatitude},${location.establishmentLongitude}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"
          >
            Conferir coordenadas no Google Maps
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        )}

        <div>
          <LoadingButton
            type="submit"
            className="btn btn-neutral cursor-pointer"
            isLoading={isSubmitting}
            loadingLabel="Salvando"
          >
            Salvar localização
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}
